import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import WatchLaterIcon from "@mui/icons-material/WatchLaterOutlined";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { QualifiedSavingsData } from "../../types";

interface QualifiedSavingsProps {
  data: QualifiedSavingsData;
  useVerbiage: "state" | "organization" | "nationwide";
  stateOrOrganization: string;
  isExport?: boolean;
}

const QualifiedSavings: React.FC<QualifiedSavingsProps> = ({
  data,
  useVerbiage,
  stateOrOrganization,
  isExport = false,
}) => {
  const getVerbiage = () => {
    return useVerbiage === "state"
      ? `By adopting digital workflows, agencies in ${stateOrOrganization} have reduced manual processes, saved time, lowered costs, and dedicated more resources to direct services.`
      : useVerbiage === "organization"
      ? `By adopting Guardify’s digital workflows, ${stateOrOrganization} has eliminated manual processes, saving time, reducing costs, and allowing more focus on delivering critical services.`
      : "By adopting digital workflows, agencies nationwide have reduced manual processes, saved time, lowered costs, and dedicated more resources to direct services.";
  };

  const tooltipContent = {
    dvdCreation:
      "Calculated using 2 hours of staff time at $28.34/hour, plus 3 shipped DVDs at $25.50 each. Total: $133.18 saved per interview.",
    longTermStorage:
      "Based on 3 DVDs per interview, each costing $1.53/year to store. Over 5 years = $22.95 saved per interview.",
    transcription:
      "Calculated using 5 hours of staff time saved per interview at $28.34/hour. 5 × $28.34 = $141.70 saved per case.",
    totalHours:
      "Each interview saves 8 staff hours: 3 from DVD creation and 5 from transcription.",
  };

  const getProgressBarWidth = (amount: number) => {
    const maxAmount = Math.max(...data.breakdown.map((item) => item.amount));
    return (amount / maxAmount) * 100;
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)} M`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const formatHours = (amount: number) => {
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + " M";
    }
    return amount.toLocaleString();
  };

  const labelIcon = {
    dvdIcon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
    longTermStorageIcon: (
      <svg
        width="16"
        height="16"
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 24 24"
        data-testid="Inventory2OutlinedIcon"
        fill="#000"
      >
        <path d="M20 2H4c-1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-1-2-2-2m-1 18H5V9h14zm1-13H4V4h16z"></path>
        <path d="M9 12h6v2H9z"></path>
      </svg>
    ),
    transcriptionIcon: (
      <svg
        width="16"
        height="16"
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 24 24"
        data-testid="DescriptionOutlinedIcon"
        fill="#000"
      >
        <path d="M8 16h8v2H8zm0-4h8v2H8zm6-10H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8zm4 18H6V4h7v5h5z"></path>
      </svg>
    ),
  };

  const getCategoryIcon = (category: string) => {
    if (category.includes("DVD Creation")) return labelIcon.dvdIcon;
    if (category.includes("Long Term Storage"))
      return labelIcon.longTermStorageIcon;
    if (category.includes("Transcription")) return labelIcon.transcriptionIcon;
    return labelIcon.dvdIcon;
  };

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{ p: isExport ? 0 : 3 }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", mb: isExport ? 0 : 1 }}
        >
          <Typography
            variant={isExport ? "body1" : "h5"}
            component="h2"
            sx={{
              mb: 1,
              width: isExport ? "100%" : "auto",
              fontWeight: 600,
              textAlign: isExport ? "center" : "left",
            }}
          >
            Qualified Savings & Time Saved
          </Typography>
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, display: isExport ? "none" : "block" }}
        >
          {getVerbiage()}
        </Typography>

        <Grid
          container
          spacing={isExport ? 0 : 3}
          sx={{ mb: isExport ? 1 : 3 }}
        >
          <Grid item xs={12} md={isExport ? 12 : 7}>
            <Box
              sx={{
                mb: isExport ? 1 : 4,
                textAlign: "center",
                display: isExport ? "flex" : "block",
              }}
            >
              <Box sx={{ width: isExport ? "50%" : "auto" }}>
                <Typography
                  variant={isExport ? "h4" : "h2"}
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: "#572a76",
                    mb: isExport ? 0 : 1,
                    fontSize: isExport ? "1.75rem" : "3rem",
                  }}
                >
                  {formatCurrency(data.totalSavings)}
                </Typography>
                <Typography
                  variant={isExport ? "body2" : "body1"}
                  color="text.secondary"
                >
                  Total Cost Savings
                </Typography>
              </Box>
              {isExport && (
                <Box
                  sx={{
                    mb: isExport ? 1 : 4,
                    textAlign: "center",
                    width: "50%",
                  }}
                >
                  <Typography
                    variant={isExport ? "h4" : "h2"}
                    component="div"
                    sx={{
                      fontWeight: 700,
                      color: "#8A4B9D",
                      mb: isExport ? 0 : 1,
                      fontSize: isExport ? "1.75rem" : "3rem",
                    }}
                  >
                    {formatHours(data.totalHours)}
                  </Typography>
                  <Typography
                    variant={isExport ? "body2" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isExport ? "0.75rem" : "0.75rem" }}
                  >
                    Total Hours Saved
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Progress bars for each category */}
            <Box sx={{ mb: isExport ? 0 : 3 }}>
              {data.breakdown.map((item, index) => {
                const IconComponent = getCategoryIcon(item.category);
                return (
                  <Box key={index} sx={{ mb: isExport ? 1 : 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      {IconComponent}
                      <Typography
                        variant={isExport ? "body2" : "body1"}
                        sx={{ fontWeight: 500, mr: 1, ml: 1 }}
                      >
                        {item.category}
                      </Typography>
                      <Tooltip
                        title={
                          item.category === "DVD Creation"
                            ? tooltipContent.dvdCreation
                            : item.category === "Long Term Storage"
                            ? tooltipContent.longTermStorage
                            : tooltipContent.transcription
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            display: isExport ? "none" : "",
                            height: "1rem",
                            width: "1rem",
                          }}
                        >
                          <InfoOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: isExport ? 1 : 2,
                      }}
                    >
                      <Box
                        sx={{
                          flexGrow: 1,
                          height: isExport ? 16 : 24,
                          bgcolor: "#F1F5F9",
                          borderRadius: 1,
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${getProgressBarWidth(item.amount)}%`,
                            height: "100%",
                            background:
                              "linear-gradient(to right, #592b7b 33.85%, #8A4B9D 100%)",
                            borderRadius: 1,
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                      <Typography
                        variant={isExport ? "body2" : "body1"}
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                          minWidth: "80px",
                        }}
                      >
                        {formatCurrency(item.amount)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {!isExport && (
            <Grid item xs={12} md={5} sx={{ mt: "auto", mb: "auto" }}>
              <Grid
                container
                spacing={2}
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-around",
                  gap: 6,
                }}
              >
                <Box
                  sx={{
                    mb: isExport ? 1 : 2,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      mt: isExport ? 1 : 0,
                    }}
                  >
                    <WatchLaterIcon
                      sx={{
                        fontSize: isExport ? 65 : 130,
                        color: "#582a77",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "center",
                      mb: 3,
                      mt: isExport ? 1 : 0,
                    }}
                  >
                    <Typography
                      variant={isExport ? "h4" : "h2"}
                      component="div"
                      sx={{
                        fontWeight: 700,
                        color: "#582a77",
                        mb: isExport ? 0 : 0.5,
                        fontSize: isExport ? "1.75rem" : "3rem",
                      }}
                    >
                      {formatHours(data.totalHours)}
                    </Typography>
                    <Typography
                      variant={isExport ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Total Hours Saved
                      <Tooltip
                        title={tooltipContent.totalHours}
                        arrow
                        placement="bottom"
                      >
                        <IconButton
                          size="small"
                          sx={{
                            ml: 0.5,
                            mt: -0.25,
                            display: isExport ? "none" : "",
                            height: "1rem",
                            width: "1rem",
                          }}
                        >
                          <InfoOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      mt: isExport ? 1 : 0,
                    }}
                  >
                    <Typography
                      variant={isExport ? "h5" : "h2"}
                      component="div"
                      sx={{
                        fontWeight: 600,
                        color: "#8A4B9D",
                        mb: isExport ? 0 : 0.5,
                      }}
                    >
                      {data.workDays.toLocaleString()}
                    </Typography>
                    <Typography
                      variant={isExport ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Work Days
                    </Typography>
                  </Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                      mt: isExport ? 1 : 0,
                    }}
                  >
                    <Typography
                      variant={isExport ? "h5" : "h2"}
                      component="div"
                      sx={{
                        fontWeight: 600,
                        color: "#8A4B9D",
                        mb: isExport ? 0 : 0.5,
                      }}
                    >
                      {data.workWeeks.toLocaleString()}
                    </Typography>
                    <Typography
                      variant={isExport ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Work Weeks
                    </Typography>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QualifiedSavings;
